import { httpClient } from '@core/http/client';

export interface UploadImagesParams {
  ticketName: string;
  defectLineName: string;
  files: File[];
  descriptions?: string[];
}

export interface UploadImagesResult {
  uploaded_count: number;
}

export class ImageAPI {
  private readonly baseEndpoint = '/api/method/upload-ticket-images';

  /**
   * POST /api/method/upload-ticket-images
   * Uploads images for a specific defect line.
   *
   * The CRM API uses standard multipart form upload (NOT GraphQL multipart spec).
   * Expected form fields:
   *   - ticket_name: string
   *   - defect_line_name: string
   *   - images: File (repeated for each file)
   *
   * Constraints:
   * - Max 5 images per defect line
   * - Max 5MB per image
   * - Only PNG and JPEG formats allowed
   */
  async upload(params: UploadImagesParams): Promise<UploadImagesResult> {
    const { ticketName, defectLineName, files } = params;

    console.log('[ImageAPI] 📤 Upload started:', {
      ticketName,
      defectLineName,
      fileCount: files.length,
      files: files.map((f) => ({
        name: f.name,
        size: f.size,
        type: f.type,
        isFile: f instanceof File,
        isBlob: f instanceof Blob,
      })),
    });

    const safeFiles = files.map((file, index) =>
      file instanceof File && file.name
        ? file
        : new File([file], `image_${index}.jpg`, {
            type: file.type || 'image/jpeg',
            lastModified: Date.now(),
          }),
    );

    const formData = new FormData();
    formData.append('ticket_name', ticketName);
    formData.append('defect_line_name', defectLineName);

    safeFiles.forEach((file) => {
      console.log(`[ImageAPI] 📎 Appending image:`, {
        name: file.name,
        size: file.size,
        type: file.type,
      });
      formData.append('images', file, file.name);
    });

    console.log('[ImageAPI] 📋 FormData entries:');
    for (const [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(`  ${key}: File(${value.name}, ${value.size}B, ${value.type})`);
      } else {
        console.log(`  ${key}: ${value}`);
      }
    }

    const response = await httpClient.post(this.baseEndpoint, formData, {
      timeout: 60_000,
    });

    console.log('[ImageAPI] ✅ Upload response:', response.status, response.data);
    return response.data.data ?? response.data;
  }
}
